import React, { useState, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Quote,
  Code2, Redo, Undo, Heading1, Heading2, Heading3, Image as ImageIcon,
  Table as TableIcon,
} from 'lucide-react';
import { FigureExtension } from './editor/FigureExtension';
import FigureInsertModal from './editor/FigureInsertModal';
import type { FigureMeta } from '../services/figureService';

interface RichTextEditorProps {
  content: string;
  onChange?: (html: string) => void;
  editable?: boolean;
  enableImages?: boolean;
  projectId?: string;
  placeholder?: string;
  className?: string;
}

type ToolbarBtnProps = {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
};

const ToolbarBtn: React.FC<ToolbarBtnProps> = ({ active, disabled, onClick, title, children }) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onMouseDown={e => { e.preventDefault(); onClick(); }}
    className={`p-1 rounded text-slate-600 hover:bg-slate-100 disabled:opacity-30 ${
      active ? 'bg-slate-200 text-slate-900' : ''
    }`}
  >
    {children}
  </button>
);

const ToolbarDivider: React.FC = () => (
  <span className="w-px h-4 bg-slate-200 mx-0.5 self-center" />
);

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  editable = true,
  enableImages = false,
  projectId,
  placeholder = '',
  className = '',
}) => {
  const [showFigureModal, setShowFigureModal] = useState(false);

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({ codeBlock: { languageClassPrefix: 'language-' } }),
        Underline,
        Image.configure({ inline: false }),
        Table.configure({ resizable: false }),
        TableRow,
        TableHeader,
        TableCell,
        Placeholder.configure({ placeholder }),
        ...(enableImages ? [FigureExtension.configure({ projectId: projectId ?? '' })] : []),
      ],
      content,
      editable,
      onUpdate: ({ editor: e }) => {
        onChange?.(e.getHTML());
      },
    },
    [editable],
  );

  // Sync content when it changes from outside (e.g. draft reload)
  React.useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== content) {
      editor.commands.setContent(content || '', false);
    }
  }, [content, editor]);

  const handleInsertFigure = useCallback(
    (meta: FigureMeta) => {
      if (!editor || !projectId) return;
      editor.chain().focus().insertFigure({
        figureId: meta.id,
        alt: meta.figure_label || meta.caption || 'Figure',
        caption: meta.caption || '',
        label: meta.figure_label || '',
      }).run();
      setShowFigureModal(false);
    },
    [editor, projectId],
  );

  if (!editor) return null;

  return (
    <div className={`flex flex-col h-full overflow-hidden ${className}`}>
      {editable && (
        <div className="flex flex-wrap items-center gap-0.5 px-2 py-1 border-b border-slate-200 bg-slate-50 flex-shrink-0">
          <ToolbarBtn
            title="Undo" disabled={!editor.can().undo()}
            onClick={() => editor.chain().focus().undo().run()}
          >
            <Undo size={13} />
          </ToolbarBtn>
          <ToolbarBtn
            title="Redo" disabled={!editor.can().redo()}
            onClick={() => editor.chain().focus().redo().run()}
          >
            <Redo size={13} />
          </ToolbarBtn>

          <ToolbarDivider />

          <ToolbarBtn
            title="Heading 1" active={editor.isActive('heading', { level: 1 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          >
            <Heading1 size={13} />
          </ToolbarBtn>
          <ToolbarBtn
            title="Heading 2" active={editor.isActive('heading', { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            <Heading2 size={13} />
          </ToolbarBtn>
          <ToolbarBtn
            title="Heading 3" active={editor.isActive('heading', { level: 3 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          >
            <Heading3 size={13} />
          </ToolbarBtn>

          <ToolbarDivider />

          <ToolbarBtn
            title="Bold" active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <Bold size={13} />
          </ToolbarBtn>
          <ToolbarBtn
            title="Italic" active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Italic size={13} />
          </ToolbarBtn>
          <ToolbarBtn
            title="Underline" active={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <UnderlineIcon size={13} />
          </ToolbarBtn>

          <ToolbarDivider />

          <ToolbarBtn
            title="Bullet list" active={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <List size={13} />
          </ToolbarBtn>
          <ToolbarBtn
            title="Numbered list" active={editor.isActive('orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered size={13} />
          </ToolbarBtn>

          <ToolbarDivider />

          <ToolbarBtn
            title="Blockquote" active={editor.isActive('blockquote')}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            <Quote size={13} />
          </ToolbarBtn>
          <ToolbarBtn
            title="Code block" active={editor.isActive('codeBlock')}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          >
            <Code2 size={13} />
          </ToolbarBtn>

          <ToolbarDivider />

          <ToolbarBtn
            title="Table"
            onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          >
            <TableIcon size={13} />
          </ToolbarBtn>

          {enableImages && projectId && (
            <>
              <ToolbarDivider />
              <ToolbarBtn title="Insert figure" onClick={() => setShowFigureModal(true)}>
                <ImageIcon size={13} />
              </ToolbarBtn>
            </>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        <EditorContent
          editor={editor}
          className="prose prose-sm max-w-none text-sm h-full px-3 py-2 outline-none [&_.ProseMirror]:min-h-full [&_.ProseMirror]:outline-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-slate-400 [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.rda-figure]:my-3 [&_.rda-figure]:text-center [&_.rda-figure__img]:inline-block [&_.rda-figure__img]:max-w-full [&_.rda-figure__img]:max-h-60 [&_.rda-figure__img]:border [&_.rda-figure__img]:border-slate-200 [&_.rda-figure__img]:rounded [&_.rda-figure__caption]:text-xs [&_.rda-figure__caption]:text-slate-500 [&_.rda-figure__caption]:mt-1"
        />
      </div>

      {showFigureModal && projectId && (
        <FigureInsertModal
          projectId={projectId}
          onInsert={handleInsertFigure}
          onClose={() => setShowFigureModal(false)}
        />
      )}
    </div>
  );
};

export default RichTextEditor;
